/**
 * Configurações → Financeiro → Metas (Rodada 3 · LOTE 4).
 * Metas por escopo (Consolidado / AG / Matheus): teto de despesa mensal, meta
 * de resultado. (O alvo do ponto de equilíbrio foi APOSENTADO na rodada
 * UIUX-CORRECOES 2026-07-10 — o PE é 100% automático.) Alimentam o
 * progresso/desvio nos
 * indicadores e a inconformidade "meta estourada". Valor vazio = sem meta.
 */
import { useEffect, useState } from 'react';

import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Alert, Button } from '@/components/ui';

type Chave = 'teto_despesa_mensal' | 'meta_resultado';
interface MetasResp {
  tetoDespesaMensalCentavos: number | null;
  metaResultadoCentavos: number | null;
}

const CAMPOS: Array<{ chave: Chave; campo: keyof MetasResp; rotulo: string; dica: string }> = [
  {
    chave: 'teto_despesa_mensal',
    campo: 'tetoDespesaMensalCentavos',
    rotulo: 'Teto de despesa mensal (R$)',
    dica: 'Despesas do DRE acima disso viram inconformidade.',
  },
  {
    chave: 'meta_resultado',
    campo: 'metaResultadoCentavos',
    rotulo: 'Meta de resultado (R$)',
    dica: 'Resultado do DRE abaixo disso vira inconformidade.',
  },
];

function centavosPraTexto(c: number | null): string {
  return c === null ? '' : (c / 100).toFixed(2).replace('.', ',');
}
function textoPraCentavos(t: string): number | null {
  const s = t.trim();
  if (s === '') return null;
  const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function FinanceiroMetasConfigCard() {
  const [escopo, setEscopo] = useState(''); // '' = consolidado
  // R5-C (C3 — modelo recorrente): '' = editar o PADRÃO (vale todo mês, daqui
  // pra frente; histórico preservado); 'yyyy-mm' = OVERRIDE só daquele mês.
  const [mesRef, setMesRef] = useState('');
  const [valores, setValores] = useState<Record<Chave, string>>({
    teto_despesa_mensal: '',
    meta_resultado: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams();
    if (escopo) p.set('empresaId', escopo);
    if (mesRef) p.set('anoMes', mesRef);
    setErr(null);
    setSalvo(false);
    Promise.resolve(api.get<MetasResp>(`/api/v1/admin/financeiro/metas?${p}`))
      .then((r) =>
        setValores({
          teto_despesa_mensal: centavosPraTexto(r?.tetoDespesaMensalCentavos ?? null),
          meta_resultado: centavosPraTexto(r?.metaResultadoCentavos ?? null),
        }),
      )
      .catch((e: Error) => setErr(e.message));
  }, [escopo, mesRef]);

  async function salvar() {
    setSalvando(true);
    setErr(null);
    setSalvo(false);
    try {
      for (const c of CAMPOS) {
        await api.put('/api/v1/admin/financeiro/metas', {
          empresaId: escopo || null,
          chave: c.chave,
          valorCentavos: textoPraCentavos(valores[c.chave]),
          ...(mesRef ? { mesReferencia: mesRef } : {}),
        });
      }
      setSalvo(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card className="mt-6" data-testid="financeiro-metas-config">
      <CardHeader>
        <CardTitle>Metas do Financeiro</CardTitle>
        <CardDescription>
          A meta é <strong>recorrente</strong>: configure uma vez e ela vale todo mês. Mudou o
          padrão? Vale daqui pra frente — os meses passados guardam a meta que valia neles. Pra um
          mês fora da curva (13º, campanha), escolha o mês em &quot;Aplicar em&quot; e salve um
          valor só pra ele. Deixe vazio pra não usar a meta.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {err && (
          <Alert variant="danger" className="mb-3">
            {err}
          </Alert>
        )}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
          <label className="text-xs text-ink-2 dark:text-ink-dark-2">
            Escopo
            <select
              value={escopo}
              onChange={(e) => setEscopo(e.target.value)}
              className="block mt-1 bg-paper dark:bg-paper-dark border border-line dark:border-line-dark rounded-lg px-2 py-2 text-sm text-ink-1 dark:text-ink-dark-1"
            >
              <option value="">Consolidado (AG + Matheus)</option>
              <option value="empresa-igm-ag">AG DOS SANTOS</option>
              <option value="empresa-igm-mussi">MATHEUS MUSSI</option>
            </select>
          </label>
          <label className="text-xs text-ink-2 dark:text-ink-dark-2">
            Aplicar em
            <input
              type="month"
              value={mesRef}
              onChange={(e) => setMesRef(e.target.value)}
              data-testid="meta-mes-referencia"
              className="block mt-1 bg-paper dark:bg-paper-dark border border-line dark:border-line-dark rounded-lg px-2 py-2 text-sm text-ink-1 dark:text-ink-dark-1"
            />
            <span className="block text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
              {mesRef ? 'só nesse mês (override)' : 'vazio = padrão (todo mês)'}
            </span>
          </label>
          {CAMPOS.map((c) => (
            <label key={c.chave} className="text-xs text-ink-2 dark:text-ink-dark-2" title={c.dica}>
              {c.rotulo}
              <input
                type="text"
                inputMode="decimal"
                placeholder="—"
                value={valores[c.chave]}
                onChange={(e) => setValores((v) => ({ ...v, [c.chave]: e.target.value }))}
                data-testid={`meta-${c.chave}`}
                className="block mt-1 w-40 bg-paper dark:bg-paper-dark border border-line dark:border-line-dark rounded-lg px-2 py-2 text-sm text-ink-1 dark:text-ink-dark-1"
              />
            </label>
          ))}
          <Button onClick={salvar} disabled={salvando} data-testid="metas-salvar">
            {salvando ? 'Salvando…' : 'Salvar metas'}
          </Button>
          {salvo && <span className="text-xs text-success">✓ salvas</span>}
        </div>
      </CardContent>
    </Card>
  );
}
